import { useCallback, useEffect, useState } from "react";
import { AccountParams } from "core/data/auth/user/types";
import { viteMode } from "core/lib/viteEnv";

type ChatlioApi = NonNullable<Window["_chatlio"]>;

// The Chatlio widget script requires window._chatlio to be a command queue when it loads:
// an array of [method, args] entries plus stub methods that push onto it. The widget
// replays the queue and then swaps in the real API.
const createCommandQueue = (): ChatlioApi => {
	const queue: [string, unknown[]][] = [];
	const queued =
		(method: string) =>
		(...args: unknown[]) => {
			queue.push([method, args]);
		};
	return Object.assign(queue, {
		configure: queued("configure"),
		identify: queued("identify"),
		track: queued("track"),
		show: queued("show"),
		hide: queued("hide"),
		// A query rather than a command, so queueing it would be pointless (nothing could
		// receive the replayed result); the widget can't be shown before it has loaded
		isShown: () => false,
		isOnline: queued("isOnline"),
		page: queued("page"),
		open: queued("open"),
		showOrHide: queued("showOrHide")
	});
};

// Loads the Chatlio live chat widget.
export const initChatlio = () => {
	try {
		// Reading localStorage throws when storage is blocked, i.e manually or private browsing on certain browsers
		const modeOverride = localStorage.getItem("MODE");
		if (viteMode !== "production" && modeOverride !== "production") {
			console.log("Chatlio is disabled in this environment");
			window._chatlio = undefined;
			return;
		}
		if (document.getElementById("chatlio-widget-embed")) {
			return;
		}
		window._chatlio = createCommandQueue();

		const script = document.createElement("script");
		script.id = "chatlio-widget-embed";
		script.src = "https://w.chatlio.com/w.chatlio-widget.js";
		script.async = true;
		script.setAttribute("data-embed-version", "2.3");
		script.setAttribute("data-widget-id", "e4e0658b-032b-42f6-5466-ca08b198a3ed");
		document.head.appendChild(script);
	} catch (error) {
		console.error("Failed to load Chatlio", error);
		window._chatlio = undefined;
	}
};

export const identifyChatlio = (account: AccountParams) => {
	if (window._chatlio !== undefined) {
		window._chatlio.identify(account.email, {
			name: account.name,
			email: account.email
		});
	}
};

export const useChatlio = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [isEnabled, setIsEnabled] = useState(window._chatlio && window._chatlio.isShown?.());

	useEffect(() => {
		if (isEnabled) {
			document.addEventListener("chatlio.expanded", () => {
				setIsOpen(true);
			});
			document.addEventListener("chatlio.collapsed", () => {
				setIsOpen(false);
			});
			return () => {
				document.removeEventListener("chatlio.expanded", () => {});
				document.removeEventListener("chatlio.collapsed", () => {});
			};
		} else {
			document.addEventListener("chatlio.ready", () => {
				setIsEnabled(true);
			});
		}
	}, [isEnabled]);

	const open = useCallback(() => {
		if (window._chatlio) {
			window._chatlio.open();
		}
	}, []);

	return {
		isOpen,
		isEnabled,
		open
	};
};
